// Lightweight API client targeting the FastAPI backend via Vite proxy (/api -> http://localhost:8000)

export type BackendDatasetCreate = {
  name: string;
  description?: string | null;
  source_type: string; // e.g. 'upload'
  schema_json: string; // JSON string of schema
  storage_path?: string | null;
};

export type BackendDatasetOut = BackendDatasetCreate & {
  id: number;
  created_at: string;
};

export type BackendChartCreate = {
  title: string;
  dataset_id: number;
  chart_type: string;
  x_field?: string | null;
  y_field?: string | null;
  aggregation?: string | null;
  config_json?: string | null;
};

export type BackendChartOut = BackendChartCreate & {
  id: number;
  created_at: string;
};

export type BackendDashboardCreate = {
  title: string;
  description?: string | null;
};

export type BackendDashboardOut = BackendDashboardCreate & {
  id: number;
  created_at: string;
};

export type BackendWidgetCreate = {
  chart_id: number;
  position_row: number;
  position_col: number;
  width: number;
  height: number;
};

export type BackendWidgetOut = BackendWidgetCreate & {
  id: number;
  dashboard_id: number;
};

const base: string = import.meta.env.VITE_API_URL || '/api';
const withCreds: RequestInit = { credentials: 'include' };

// Simple token management
// Simple token management
// We now rely on HttpOnly cookies, so we don't store the token in localStorage.
export const setAuthToken = (t: string | null) => {
  // No-op: Token is handled via HttpOnly cookie set by backend
};
export const getAuthToken = (): string | null => null;

const headersWithAuth = (headers: Record<string, string> = {}) => {
  // No Bearer token needed; cookies are sent automatically
  const all: Record<string, string> = { ...headers };
  const csrf = getCsrfToken();
  if (csrf) all['X-CSRF-Token'] = csrf;
  return all;
};

function getCsrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(/(?:^|; )weaver_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

// Auth endpoints
export type RegisterPayload = { email: string; password: string; display_name?: string };
export type LoginPayload = { email: string; password: string };
export type User = { id: number; email: string; name?: string; display_name?: string; avatar_url?: string; is_active: boolean; created_at: string; subscription_tier: string; plan_type?: string; trial_expires_at?: string; google_account_linked?: boolean };
export type TokenResponse = { access_token: string; token_type: string; user: User };

export const register = async (payload: RegisterPayload): Promise<TokenResponse> => {
  const r = await fetch(`${base}/auth/register`, {
    method: 'POST',
    headers: headersWithAuth({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
    ...withCreds,
  });
  return asJson(r);
};

export const login = async (payload: LoginPayload): Promise<TokenResponse> => {
  const r = await fetch(`${base}/auth/login`, {
    method: 'POST',
    headers: headersWithAuth({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
    ...withCreds,
  });
  return asJson(r);
};

export const me = async (): Promise<User> => {
  const r = await fetch(`${base}/auth/me`, { headers: headersWithAuth(), ...withCreds });
  return asJson(r);
};

export const updateMe = async (payload: Partial<{ display_name: string; name: string; avatar_url: string }>): Promise<User> => {
  const r = await fetch(`${base}/auth/me`, {
    method: 'PUT',
    headers: headersWithAuth({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
    ...withCreds,
  });
  return asJson(r);
};

export const updateAvatar = async (avatar_url: string): Promise<User> => {
  const r = await fetch(`${base}/auth/me`, {
    method: 'PUT',
    headers: headersWithAuth({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ avatar_url }),
    ...withCreds,
  });
  return asJson(r);
};

export const changePassword = async (current_password: string, new_password: string) => {
  const r = await fetch(`${base}/auth/change-password`, {
    method: 'POST',
    headers: headersWithAuth({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ current_password, new_password }),
    ...withCreds,
  });
  return asJson(r);
};

export const forgotPassword = async (email: string) => {
  const r = await fetch(`${base}/auth/forgot-password`, {
    method: 'POST',
    headers: headersWithAuth({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ email }),
    ...withCreds,
  });
  return asJson(r);
};

export const resetPassword = async (token: string, password: string) => {
  const r = await fetch(`${base}/auth/reset-password`, {
    method: 'POST',
    headers: headersWithAuth({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ token, password }),
    ...withCreds,
  });
  return asJson(r);
};

export const getPlans = async () => {
  const r = await fetch(`${base}/billing/plans`, { headers: headersWithAuth(), ...withCreds });
  return asJson(r);
};

export const upgradePlan = async () => {
  const r = await fetch(`${base}/billing/upgrade`, {
    method: 'POST',
    headers: headersWithAuth(),
    ...withCreds,
  });
  return asJson(r);
};

const asJson = (r: Response) => {
  if (!r.ok) throw new Error(`API ${r.status}`);
  return r.json();
};

export const pingBackend = async (): Promise<boolean> => {
  try {
    const r = await fetch(`${base}/health`, { ...withCreds });
    return r.ok;
  } catch {
    return false;
  }
};

export const createDataset = async (payload: BackendDatasetCreate): Promise<BackendDatasetOut> => {
  const r = await fetch(`${base}/datasets/`, {
    method: 'POST',
    headers: headersWithAuth({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
    ...withCreds,
  });
  return asJson(r);
};

export const createChart = async (payload: BackendChartCreate): Promise<BackendChartOut> => {
  const r = await fetch(`${base}/charts/`, {
    method: 'POST',
    headers: headersWithAuth({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
    ...withCreds,
  });
  return asJson(r);
};

export const createDashboard = async (payload: BackendDashboardCreate): Promise<BackendDashboardOut> => {
  const r = await fetch(`${base}/dashboards/`, {
    method: 'POST',
    headers: headersWithAuth({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
    ...withCreds,
  });
  return asJson(r);
};

export const addDashboardWidget = async (
  dashboardId: number,
  payload: BackendWidgetCreate,
): Promise<BackendWidgetOut> => {
  const r = await fetch(`${base}/dashboards/${dashboardId}/widgets`, {
    method: 'POST',
    headers: headersWithAuth({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
    ...withCreds,
  });
  return asJson(r);
};
